/** 現行の永続化スキーマ版。バージョンが将来この値を超えていれば「未来のデータ」として扱う。 */
export const CURRENT_PROGRESS_SCHEMA_VERSION = 1;

/** レベル総数（F-002）。全制覇スタンプの判定基準として使う。 */
export const TOTAL_LEVELS = 7;

/**
 * 到達状況とスタンプの記録。**要素が減らない集合**として不変条件を守る
 * （ADR-007）。`clearedDrills`/`stamps` はどちらも要素が増えるだけであり、
 * 削除操作はこのモジュールが公開しない。
 */
export interface Progress {
  readonly schemaVersion: number;
  readonly clearedDrills: ReadonlySet<string>;
  readonly stamps: ReadonlySet<string>;
  readonly updatedAt: string;
}

export function createProgress(updatedAt: string = new Date().toISOString()): Progress {
  return {
    schemaVersion: CURRENT_PROGRESS_SCHEMA_VERSION,
    clearedDrills: new Set(),
    stamps: new Set(),
    updatedAt,
  };
}

/** 全7レベルのスタンプを獲得しているかを`stamps`の要素数から導出する（独立に保持しない）。 */
export function hasGrandStamp(progress: Progress): boolean {
  return progress.stamps.size >= TOTAL_LEVELS;
}

/** 演習クリアを記録する。既にクリア済みなら何もしない（既存の`updatedAt`を保つ）。 */
export function withClearedDrill(
  progress: Progress,
  drillId: string,
  updatedAt: string = new Date().toISOString(),
): Progress {
  if (progress.clearedDrills.has(drillId)) {
    return progress;
  }
  const clearedDrills = new Set(progress.clearedDrills);
  clearedDrills.add(drillId);
  return { ...progress, clearedDrills, updatedAt };
}

/** スタンプ獲得を記録する。既に獲得済みなら何もしない（既存の`updatedAt`を保つ）。 */
export function withStamp(
  progress: Progress,
  levelId: string,
  updatedAt: string = new Date().toISOString(),
): Progress {
  if (progress.stamps.has(levelId)) {
    return progress;
  }
  const stamps = new Set(progress.stamps);
  stamps.add(levelId);
  return { ...progress, stamps, updatedAt };
}

/**
 * 2つのProgressを集合の合併で統合する（ADR-007）。**可換・結合的・冪等**。
 * `updatedAt`は両者の新しい方（文字列比較で決まるISO8601の大小）を採用することで、
 * 呼び出し時刻に依存しない純粋な演算にしている（同じ入力なら常に同じ出力になる）。
 */
export function mergedWith(a: Progress, b: Progress): Progress {
  const clearedDrills = new Set([...a.clearedDrills, ...b.clearedDrills]);
  const stamps = new Set([...a.stamps, ...b.stamps]);
  return {
    schemaVersion: Math.max(a.schemaVersion, b.schemaVersion),
    clearedDrills,
    stamps,
    updatedAt: a.updatedAt >= b.updatedAt ? a.updatedAt : b.updatedAt,
  };
}
